import React, { useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { XIcon, Star, CheckIcon, Info } from 'lucide-react';

export interface GuideItem {
  icon?: 'bullet' | 'star' | 'check';
  boldText?: string;
  text: string;
}

interface GuideTooltipProps {
  items: GuideItem[];
  title?: string;
  storageKey?: string; // Optional key to store dismissed state in localStorage
  containerId?: string; // ID of the container where tooltip should render
}

const GuideTooltip: React.FC<GuideTooltipProps> = ({ items, title, storageKey, containerId }) => {
  const [isOpen, setIsOpen] = useState(() => {
    if (storageKey) {
      const saved = localStorage.getItem(storageKey);
      if (saved === null) {
        // First visit to this specific guide - check if user has visited any guide before
        const hasVisitedAnyGuide = Object.keys(localStorage).some(key => key.endsWith('_guide'));
        return !hasVisitedAnyGuide; // Open on first ever visit, closed otherwise
      }
      return saved === '1'; // Explicitly opened
    }
    return false; // Default to closed
  });

  const handleClose = () => {
    setIsOpen(false);
    if (storageKey) {
      localStorage.setItem(storageKey, '0');
    }
  };

  const handleToggle = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    if (storageKey) {
      localStorage.setItem(storageKey, newIsOpen ? '1' : '0');
    }
  };

  const renderIcon = (iconType?: 'bullet' | 'star' | 'check') => {
    switch (iconType) {
      case 'star':
        return <Star className="h-4 w-4" />;
      case 'check':
        return <CheckIcon className="h-4 w-4" />;
      case 'bullet':
      default:
        return <span className="text-blue-900">•</span>;
    }
  };

  // Render tooltip content to external container
  useEffect(() => {
    if (!containerId) return;

    const container = document.getElementById(containerId);
    if (!container) return;

    // Clear container first
    container.innerHTML = '';

    if (isOpen) {
      const root = createRoot(container);
      root.render(
        <div className="w-full mt-2">
          <div className="w-full p-6 bg-blue-100 text-blue-800 font-semibold rounded-lg relative gap-4 flex flex-col">
            {/* Close Button */}
            <div
              className="absolute top-2 right-2 cursor-pointer w-5 h-5 flex items-center justify-center"
              onClick={() => {
                setIsOpen(false);
                if (storageKey) {
                  localStorage.setItem(storageKey, '0');
                }
              }}
            >
              <XIcon className="h-5 w-5" aria-hidden="true" />
            </div>

            {/* Title */}
            {title && (
              <div className="text-base font-semibold pr-8">{title}</div>
            )}

            {/* Items List */}
            <div className="flex flex-col pl-1 pr-8 text-base font-normal">
              {items.map((item, index) => (
                <div key={index} className="flex items-top gap-3">
                  <div className="h-6 w-5 px-1 flex items-center justify-center flex-shrink-0">
                    <div className="text-blue-900 h-4 w-full flex items-center justify-center">
                      {renderIcon(item.icon)}
                    </div>
                  </div>
                  <div className="m-0" style={{ margin: 0 }}>
                    {item.boldText && <span className="font-medium">{item.boldText}:</span>} {item.text}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    return () => {
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [isOpen, containerId, items, title, storageKey]);

      return (
      <span className="inline-block ml-2">
      {!isOpen && (
        <button
          type="button"
          onClick={handleToggle}
          className="flex items-center justify-center text-blue-600 hover:text-blue-800 cursor-pointer transition-colors duration-200 w-4 h-4"
          aria-label={isOpen ? "Hide guide" : "Show guide"}
        >
          <Info className="h-4 w-4" />
        </button>
      )}
    </span>

  );
};

export default GuideTooltip;
